import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Clock, Users, Wifi, WifiOff } from 'lucide-react';

const joinCode = 'bdx43gd';
const serverEndpoint = `https://servers-frontend.fivem.net/api/servers/single/${joinCode}`;
const serverProxyEndpoint = '/api/fivem/server';

function formatTime(date) {
  return new Intl.DateTimeFormat('ar-SA', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export default function About() {
  const videoRef = useRef(null);
  const [serverStatus, setServerStatus] = useState({
    loading: true,
    online: false,
    players: 0,
    maxPlayers: 0,
    lastSeen: '--',
  });

  useEffect(() => {
    const playVideo = async () => {
      if (!videoRef.current) return;

      try {
        videoRef.current.defaultMuted = true;
        videoRef.current.muted = true;
        await videoRef.current.play();
      } catch (error) {
        console.error('Autoplay failed:', error);
      }
    };

    const timeoutId = setTimeout(playVideo, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    let ignore = false;

    const requestServerPayload = async () => {
      const endpoints = [
        `${serverEndpoint}?t=${Date.now()}`,
        `${serverProxyEndpoint}?t=${Date.now()}`,
      ];

      let lastError;

      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            cache: 'no-store',
          });

          if (!response.ok) {
            throw new Error(`Status ${response.status}`);
          }

          return response.json();
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError;
    };

    const loadServerStatus = async () => {
      try {
        const payload = await requestServerPayload();
        const data = payload.Data || {};
        const players = Number(data.clients ?? 0);
        const maxPlayers = Number(data.sv_maxclients ?? data.svMaxclients ?? 0);

        if (!ignore) {
          setServerStatus({
            loading: false,
            online: true,
            players,
            maxPlayers,
            lastSeen: formatTime(new Date()),
          });
        }
      } catch (error) {
        if (!ignore) {
          setServerStatus((current) => ({
            ...current,
            loading: false,
            online: false,
            players: 0,
            maxPlayers: 0,
            lastSeen: formatTime(new Date()),
          }));
        }
      }
    };

    loadServerStatus();
    const intervalId = setInterval(loadServerStatus, 10000);

    return () => {
      ignore = true;
      clearInterval(intervalId);
    };
  }, []);


  const playersPercent = serverStatus.maxPlayers > 0
    ? Math.min(100, Math.round((serverStatus.players / serverStatus.maxPlayers) * 100))
    : 0;

  return (
    <section id="عن-المقاطعة" className="pt-32 pb-24 min-h-screen bg-black relative flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(17,184,173,0.12),transparent_32%),radial-gradient(circle_at_80%_80%,rgba(11,111,105,0.18),transparent_34%)] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:w-1/2"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              عن <span className="text-neon-cyan">مقاطعة الراية</span>
            </h2>
            <p className="text-gray-400 text-lg leading-loose mb-8">
              مجتمع حياة واقعية يركز على النظام، الواقعية، والاحترام. نقدم تجربة لعب متوازنة مع قطاعات فعالة وإدارة حاضرة لتطوير المقاطعة باستمرار.
            </p>

            <div className="glass-panel rounded-2xl border border-neon-cyan/20 p-6 shadow-neon">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <p className="text-gray-400 text-sm mb-1">حالة السيرفر</p>
                  <h3 className="text-2xl font-black text-white">
                    {serverStatus.loading ? 'جاري التحقق...' : serverStatus.online ? 'متصل' : 'غير متاح'}
                  </h3>
                </div>
                <div className={`h-12 w-12 rounded-full flex items-center justify-center border ${serverStatus.online ? 'border-neon-cyan text-neon-cyan bg-neon-cyan/10' : 'border-red-400/40 text-red-300 bg-red-500/10'}`}>
                  {serverStatus.online ? <Wifi size={24} /> : <WifiOff size={24} />}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="rounded-xl bg-black/35 border border-white/10 p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                    <Users size={16} />
                    <span>اللاعبين</span>
                  </div>
                  <p className="text-2xl font-black text-white">
                    {serverStatus.online ? serverStatus.players : '--'}
                    <span className="text-gray-500 text-base"> / {serverStatus.online ? serverStatus.maxPlayers : '--'}</span>
                  </p>
                </div>

                <div className="rounded-xl bg-black/35 border border-white/10 p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                    <Clock size={16} />
                    <span>آخر تحديث</span>
                  </div>
                  <p className="text-2xl font-black text-white">{serverStatus.lastSeen}</p>
                </div>
              </div>

              <div className="h-3 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-neon-cyan shadow-neon transition-all duration-700"
                  style={{ width: `${playersPercent}%` }}
                />
              </div>

              <div className="mt-5 flex items-center gap-2 text-sm text-gray-400">
                <Activity size={16} className="text-neon-cyan" />
                <span>Connect: {joinCode}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:w-1/2 w-full"
          >
            <div className="aspect-video rounded-3xl border border-neon-cyan/20 glass-panel flex items-center justify-center relative overflow-hidden group shadow-neon">
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                onCanPlay={(e) => e.target.play()}
                className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-1000"
              >
                <source src="/lodng-web.mp4" type="video/mp4" />
              </video>

              <div className="absolute inset-0 bg-gradient-to-tr from-black/65 via-transparent to-neon-cyan/20 pointer-events-none" />
              <div className="absolute inset-0 border border-neon-cyan/30 m-4 rounded-2xl z-10 pointer-events-none transition-all duration-500 group-hover:m-2" />


              <div className="absolute bottom-6 right-6 z-10 bg-black/60 backdrop-blur-md px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-neon-cyan animate-pulse" />
                <span className="text-neon-cyan font-mono text-sm tracking-widest mt-1">LIVE_FEED</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
